import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  Building2,
  ExternalLink,
  Filter,
  Mail,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  AlertCircle,
  Beaker,
  FileText,
  Copy,
  ChevronDown,
  Loader2,
  Activity,
  Target,
  Sparkles,
  Trash2,
  Globe,
  Newspaper,
  Settings,
  Play,
  MailPlus,
  Upload,
  Download,
  FileSpreadsheet,
  Plus,
  X,
  UserPlus,
  Link2,
  Unlink,
  HelpCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_STATUS_STYLES = {
  "New": { bg: "bg-blue-100", text: "text-blue-800", icon: AlertCircle },
  "Watching": { bg: "bg-purple-100", text: "text-purple-800", icon: Eye },
  "Contacted": { bg: "bg-green-100", text: "text-green-800", icon: CheckCircle },
  "In Progress": { bg: "bg-yellow-100", text: "text-yellow-800", icon: Clock },
  "Not Relevant": { bg: "bg-slate-100", text: "text-slate-500", icon: XCircle }
};

const EVENT_TYPE_ICONS = {
  "New Trial Registration": Beaker,
  "Phase 1/2 Positive Data": TrendingUp,
  "Phase 2a Enrollment": TrendingUp,
  "Phase 3 Starting": TrendingUp,
  "Phase 3 Complete": CheckCircle,
  "Phase 1/2 Enrolling": Clock,
  "Phase 2 Data Coming": Clock,
  "FDA Breakthrough Designation": Target,
  "IND Filing": FileText,
  "Partnership Announcement": Building2,
  "Other": Activity
};

const THERAPEUTIC_AREAS = [
  "Dermatology", "Rheumatology", "Pulmonology / Respiratory", "Neurology",
  "Cardiology", "Psychiatry", "Gastroenterology", "General / Internal Medicine",
  "Allergy / Immunology", "Pediatrics", "Endocrinology", "Ophthalmology",
  "Cell & Gene Therapy", "Immunology", "Rare Diseases"
];

const EVENT_TYPES = [
  "Phase 1 Enrolling", "Phase 1/2 Enrolling", "Phase 2 Enrolling", "Phase 2a Enrollment",
  "Phase 3 Starting", "Phase 3 Complete", "Phase 2 Data Coming",
  "FDA Breakthrough Designation", "Fast Track Designation", "IND Filing",
  "Partnership Announcement", "New Trial Registration", "Other"
];

const LEAD_CONNECTION_STATUSES = {
  "New": { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  "Contacted": { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  "Responded": { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  "Meeting Set": { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  "Not Interested": { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
  "Connected": { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" }
};

const SIGNAL_TYPES = [
  { key: "S1", label: "Phase 2 completion", timeline: "3-6 months", color: "bg-blue-100 text-blue-800 border-blue-200", sources: "ClinicalTrials.gov + PubMed" },
  { key: "S2", label: "Phase 3 IND filed", timeline: "1-3 months", color: "bg-red-100 text-red-800 border-red-200", sources: "ClinicalTrials.gov" },
  { key: "S3", label: "FDA designation", timeline: "1-4 months", color: "bg-orange-100 text-orange-800 border-orange-200", sources: "FDA.gov RSS feed" },
  { key: "S4", label: "Earnings pipeline mention", timeline: "3-9 months", color: "bg-purple-100 text-purple-800 border-purple-200", sources: "SEC EDGAR / IR pages" },
  { key: "S5", label: "Conference abstract", timeline: "6-12 months", color: "bg-teal-100 text-teal-800 border-teal-200", sources: "PubMed / conference sites" },
  { key: "S6", label: "CRO partnership announced", timeline: "1-3 months", color: "bg-amber-100 text-amber-800 border-amber-200", sources: "PR Newswire / LinkedIn" },
];

const getSignalInfo = (key) => SIGNAL_TYPES.find(s => s.key === key);

// Helper function to format dates correctly (avoids timezone offset issues)
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    // For ISO datetime strings, extract just the date part
    if (dateStr.includes("T")) {
      const datePart = dateStr.split("T")[0];
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    // For date-only strings
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    }
    return new Date(dateStr).toLocaleDateString();
  } catch {
    return dateStr;
  }
};

export default function Opportunities() {
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchRunning, setSearchRunning] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newOpp, setNewOpp] = useState({
    company_name: "", event_type: "", indication: "", therapeutic_area: "",
    current_phase: "", source: "", source_url: "", notes: "",
    signal_type: "", signal_source: "", timeline_to_site_selection: ""
  });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [showAddLeadInline, setShowAddLeadInline] = useState(false);
  const [showAddCompanyInline, setShowAddCompanyInline] = useState(false);
  const [newInlineLead, setNewInlineLead] = useState({ first_name: "", last_name: "", email: "", role: "" });
  const [newInlineCompany, setNewInlineCompany] = useState({ name: "", tier: "Unranked" });
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    search: "",
    action_status: searchParams.get("status") || "all",
    therapeutic_area: "all",
    source: "all",
    signal_type: "all",
    internal_only: searchParams.get("in_crm") === "true"
  });
  const [availableSources, setAvailableSources] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [selectedOpps, setSelectedOpps] = useState(new Set()); // For bulk selection
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showEmailRulesDialog, setShowEmailRulesDialog] = useState(false);
  const [emailRules, setEmailRules] = useState([]);
  const [processingEmails, setProcessingEmails] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showLeadManager, setShowLeadManager] = useState(false);
  const [managingOpp, setManagingOpp] = useState(null);
  const [leadSearchQuery, setLeadSearchQuery] = useState("");
  const [leadSearchResults, setLeadSearchResults] = useState([]);
  const [linkingLead, setLinkingLead] = useState(false);
  const [showWorkflowGuide, setShowWorkflowGuide] = useState(false);
  const [indicationOptions, setIndicationOptions] = useState([]);
  const [showIndicationDropdown, setShowIndicationDropdown] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editCompanyOptions, setEditCompanyOptions] = useState([]);
  const [showEditCompanyDropdown, setShowEditCompanyDropdown] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);

  const fetchOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.action_status !== "all") params.append("action_status", filters.action_status);
      if (filters.therapeutic_area !== "all") params.append("therapeutic_area", filters.therapeutic_area);
      if (filters.source !== "all") params.append("source", filters.source);
      if (filters.signal_type !== "all") params.append("signal_type", filters.signal_type);
      if (filters.internal_only) params.append("internal_only", "true");
      params.append("limit", "100");

      const [oppsRes, statsRes] = await Promise.all([
        axios.get(`${API}/opportunities?${params.toString()}`),
        axios.get(`${API}/opportunities/stats`)
      ]);
      
      setOpportunities(oppsRes.data);
      setStats(statsRes.data);
      
      // Extract unique sources for filter dropdown
      const sources = [...new Set(oppsRes.data.map(o => o.source).filter(Boolean))];
      setAvailableSources(sources.sort());
    } catch (error) {
      toast.error("Failed to load opportunities");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  // Fetch company names for autocomplete
  const searchCompanies = async (query) => {
    if (!query || query.length < 2) {
      setCompanyOptions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/companies/names?search=${encodeURIComponent(query)}`);
      setCompanyOptions(response.data.companies || []);
      setShowCompanyDropdown(true);
    } catch (error) {
      console.error("Error fetching companies", error);
    }
  };

  // Fetch leads for the selected company
  const fetchLeadsForCompany = async (companyName) => {
    if (!companyName) {
      setAllLeads([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/leads?company=${encodeURIComponent(companyName)}&limit=100`);
      setAllLeads(response.data || []);
    } catch (error) {
      console.error("Error fetching leads", error);
    }
  };

  // Fetch indication options for autocomplete
  const fetchIndications = async () => {
    try {
      const response = await axios.get(`${API}/opportunities/indications/list`);
      setIndicationOptions(response.data.indications || []);
    } catch (error) {
      console.error("Error fetching indications", error);
    }
  };

  useEffect(() => { fetchIndications(); }, []);

  const filteredIndications = indicationOptions.filter(ind =>
    ind.toLowerCase().includes((newOpp.indication || "").toLowerCase())
  );

  // Search companies for editing company name on existing opp
  const searchEditCompanies = async (query) => {
    if (!query || query.length < 2) {
      setEditCompanyOptions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/companies/names?search=${encodeURIComponent(query)}`);
      setEditCompanyOptions(response.data.companies || []);
      setShowEditCompanyDropdown(true);
    } catch (error) {
      console.error("Error fetching companies", error);
    }
  };

  // Save company name edit
  const saveCompanyEdit = async (oppId) => {
    if (!editCompanyName.trim()) return;
    setSavingCompany(true);
    try {
      await axios.put(`${API}/opportunities/${oppId}`, { company_name: editCompanyName.trim() });
      toast.success("Company name updated");
      setEditingCompany(null);
      setEditCompanyName("");
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to update company name");
    } finally {
      setSavingCompany(false);
    }
  };

  // Filter leads based on search
  const filteredLeads = allLeads.filter(lead => {
    if (!leadSearch) return true;
    const searchLower = leadSearch.toLowerCase();
    return (
      (lead.contact || "").toLowerCase().includes(searchLower) ||
      (lead.first_name || "").toLowerCase().includes(searchLower) ||
      (lead.last_name || "").toLowerCase().includes(searchLower) ||
      (lead.email || "").toLowerCase().includes(searchLower)
    );
  });

  const runSearch = async (type) => {
    try {
      setSearchRunning(true);
      let endpoint = "/opportunities/search/";
      
      switch (type) {
        case "clinical-trials":
          endpoint += "clinical-trials?";
          THERAPEUTIC_AREAS.forEach(area => {
            endpoint += `therapeutic_areas=${encodeURIComponent(area)}&`;
          });
          endpoint += "max_results=50";
          break;
        case "ai-news":
          endpoint += "ai-news";
          break;
        case "web-news":
          endpoint += "web-news";
          break;
        case "full":
          endpoint += "run-full";
          break;
        default:
          return;
      }

      const response = await axios.post(`${API}${endpoint}`);
      toast.success(`Search complete: ${response.data.opportunities_created || 0} new opportunities found`);
      fetchOpportunities();
    } catch (error) {
      toast.error("Search failed");
      console.error(error);
    } finally {
      setSearchRunning(false);
    }
  };

  const updateOpportunityStatus = async (id, status) => {
    try {
      await axios.put(`${API}/opportunities/${id}`, { action_status: status });
      toast.success(`Status updated to ${status}`);
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteOpportunity = async (id) => {
    try {
      await axios.delete(`${API}/opportunities/${id}`);
      toast.success("Opportunity deleted");
      setDeleteConfirm(null);
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to delete opportunity");
    }
  };

  const bulkDeleteBySource = async (source) => {
    try {
      const response = await axios.delete(`${API}/opportunities/bulk/by-source?source=${encodeURIComponent(source)}`);
      toast.success(`Deleted ${response.data.deleted_count} opportunities from ${source}`);
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to bulk delete");
    }
  };

  const toggleSelectOpp = (id) => {
    setSelectedOpps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAllOpps = () => {
    if (selectedOpps.size === opportunities.length) {
      setSelectedOpps(new Set());
    } else {
      setSelectedOpps(new Set(opportunities.map(o => o.id)));
    }
  };

  const bulkDeleteSelected = async () => {
    if (selectedOpps.size === 0) return;
    
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedOpps);
      const response = await axios.post(`${API}/opportunities/bulk/delete`, { ids });
      toast.success(`Deleted ${response.data.deleted_count} opportunities`);
      setSelectedOpps(new Set());
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to bulk delete");
    } finally {
      setBulkDeleting(false);
    }
  };

  const generateEmailTemplate = async (opp, style = "initial") => {
    try {
      setEmailLoading(true);
      setSelectedOpp(opp);
      setShowEmailDialog(true);
      
      // Try learned template first (uses patterns from actual BD emails)
      try {
        const response = await axios.post(`${API}/opportunities/email-template/learned?opportunity_id=${opp.id}&style=${style}`);
        setEmailTemplate(response.data);
        return;
      } catch {
        // Fall back to regular AI template
      }
      
      const response = await axios.post(`${API}/opportunities/email-template`, {
        opportunity_id: opp.id,
        template_style: style === "initial" ? "professional" : "casual"
      });
      
      setEmailTemplate(response.data);
    } catch (error) {
      // Fall back to static template
      const response = await axios.get(`${API}/opportunities/${opp.id}/static-template`);
      setEmailTemplate(response.data);
    } finally {
      setEmailLoading(false);
    }
  };

  const learnFromEmails = async () => {
    try {
      toast.info("Analyzing BD team emails to learn patterns...");
      const response = await axios.post(`${API}/opportunities/email-template/learn`);
      if (response.data.status === "success") {
        toast.success(`Learned patterns from ${response.data.emails_analyzed} emails!`);
      } else {
        toast.warning(response.data.message || "Could not learn patterns - check email sync");
      }
    } catch (error) {
      toast.error("Failed to learn from emails");
    }
  };

  const processEmailsForOpportunities = async () => {
    try {
      setProcessingEmails(true);
      toast.info("Processing synced emails for opportunities...");
      const response = await axios.post(`${API}/opportunities/email-parser/process`);
      if (response.data.opportunities_created > 0) {
        toast.success(`Created ${response.data.opportunities_created} new opportunities from emails!`);
        fetchOpportunities();
      } else {
        toast.info(`Processed ${response.data.processed} emails - no new opportunities found`);
      }
    } catch (error) {
      toast.error("Failed to process emails");
    } finally {
      setProcessingEmails(false);
    }
  };

  const fetchEmailRules = async () => {
    try {
      const response = await axios.get(`${API}/opportunities/email-parser/rules`);
      setEmailRules(response.data.rules || []);
    } catch (error) {
      console.error("Failed to fetch email rules", error);
    }
  };

  const toggleEmailRule = async (ruleId) => {
    const updatedRules = emailRules.map(r => 
      r.id === ruleId ? { ...r, enabled: !r.enabled } : r
    );
    setEmailRules(updatedRules);
    try {
      await axios.put(`${API}/opportunities/email-parser/rules`, { rules: updatedRules });
      toast.success("Rule updated");
    } catch (error) {
      toast.error("Failed to update rule");
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    setImportPreview(null);
    
    // Preview the import
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/opportunities/import/preview`, formData);
      setImportPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview file");
      setImportFile(null);
    }
  };

  const executeImport = async () => {
    if (!importFile) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(`${API}/opportunities/import/execute?skip_duplicates=true`, formData);
      toast.success(`Imported ${response.data.created} opportunities! (${response.data.skipped_duplicates} duplicates skipped)`);
      setShowImportDialog(false);
      setImportFile(null);
      setImportPreview(null);
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`${API}/opportunities/import/template`, '_blank');
  };

  const createOpportunity = async () => {
    // Use inline company name if the user is adding a new company
    const companyName = showAddCompanyInline && newInlineCompany.name
      ? newInlineCompany.name
      : newOpp.company_name;

    if (!companyName || !newOpp.event_type) {
      toast.error("Company name and event type are required");
      return;
    }

    try {
      let leadIds = [...selectedLeads];

      // If inline lead form is filled, create the lead first
      if (showAddLeadInline && (newInlineLead.first_name || newInlineLead.last_name || newInlineLead.email)) {
        try {
          const leadResp = await axios.post(`${API}/leads`, {
            first_name: newInlineLead.first_name,
            last_name: newInlineLead.last_name,
            email: newInlineLead.email,
            role: newInlineLead.role,
            company: companyName,
            company_tier: showAddCompanyInline ? newInlineCompany.tier : undefined
          });
          if (leadResp.data?.id) {
            leadIds.push(leadResp.data.id);
            toast.success(`Lead "${leadResp.data.contact}" created`);
          }
        } catch (leadErr) {
          const msg = leadErr.response?.data?.detail || "Failed to create lead";
          toast.error(msg);
          return;
        }
      }

      // Create the opportunity with linked leads
      await axios.post(`${API}/opportunities`, {
        ...newOpp,
        company_name: companyName,
        source: newOpp.source || "Manual Entry",
        action_status: "New",
        linked_lead_ids: leadIds.length > 0 ? leadIds : undefined,
        signal_type: newOpp.signal_type || undefined,
        signal_source: newOpp.signal_source || undefined,
        timeline_to_site_selection: newOpp.timeline_to_site_selection || undefined
      });

      toast.success("Opportunity created!");
      setShowAddDialog(false);
      setNewOpp({
        company_name: "", event_type: "", indication: "", therapeutic_area: "",
        current_phase: "", source: "", source_url: "", notes: "",
        signal_type: "", signal_source: "", timeline_to_site_selection: ""
      });
      setSelectedLeads([]);
      setAllLeads([]);
      setShowAddLeadInline(false);
      setShowAddCompanyInline(false);
      setNewInlineLead({ first_name: "", last_name: "", email: "", role: "" });
      setNewInlineCompany({ name: "", tier: "Unranked" });
      fetchOpportunities();
    } catch (error) {
      toast.error("Failed to create opportunity");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  // ============ Lead Connection Management ============
  const openLeadManager = (opp) => {
    setManagingOpp(opp);
    setShowLeadManager(true);
    setLeadSearchQuery("");
    setLeadSearchResults([]);
  };

  const searchLeadsForLinking = async (query) => {
    setLeadSearchQuery(query);
    if (!query || query.length < 2) {
      setLeadSearchResults([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/leads?search=${encodeURIComponent(query)}&limit=10`);
      setLeadSearchResults(response.data || []);
    } catch (error) {
      console.error("Error searching leads", error);
    }
  };

  const linkLead = async (leadId) => {
    if (!managingOpp) return;
    setLinkingLead(true);
    try {
      const resp = await axios.post(`${API}/opportunities/${managingOpp.id}/leads`, {
        lead_id: leadId,
        status: "New"
      });
      toast.success(`Linked ${resp.data.lead_name}`);
      // Refresh opportunities and update managingOpp
      await fetchOpportunities();
      const updatedOpps = await axios.get(`${API}/opportunities?search=${encodeURIComponent(managingOpp.company_name)}&limit=50`);
      const updated = (updatedOpps.data || []).find(o => o.id === managingOpp.id);
      if (updated) setManagingOpp(updated);
      setLeadSearchQuery("");
      setLeadSearchResults([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to link lead");
    } finally {
      setLinkingLead(false);
    }
  };

  const unlinkLead = async (leadId) => {
    if (!managingOpp) return;
    try {
      await axios.delete(`${API}/opportunities/${managingOpp.id}/leads/${leadId}`);
      toast.success("Lead unlinked");
      await fetchOpportunities();
      const updatedOpps = await axios.get(`${API}/opportunities?search=${encodeURIComponent(managingOpp.company_name)}&limit=50`);
      const updated = (updatedOpps.data || []).find(o => o.id === managingOpp.id);
      if (updated) setManagingOpp(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlink lead");
    }
  };

  const updateConnectionStatus = async (leadId, newStatus) => {
    if (!managingOpp) return;
    try {
      await axios.put(`${API}/opportunities/${managingOpp.id}/leads/${leadId}`, { status: newStatus });
      // Update local state
      await fetchOpportunities();
      const updatedOpps = await axios.get(`${API}/opportunities?search=${encodeURIComponent(managingOpp.company_name)}&limit=50`);
      const updated = (updatedOpps.data || []).find(o => o.id === managingOpp.id);
      if (updated) setManagingOpp(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  const StatusBadge = ({ status }) => {
    const style = ACTION_STATUS_STYLES[status] || ACTION_STATUS_STYLES["New"];
    const Icon = style.icon;
    return (
      <Badge className={cn("gap-1", style.bg, style.text)}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const EventTypeIcon = ({ type }) => {
    const Icon = EVENT_TYPE_ICONS[type] || EVENT_TYPE_ICONS["Other"];
    return <Icon className="h-4 w-4 text-slate-500" />;
  };

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">BD Opportunities</h1>
          <p className="text-muted-foreground">
            Track and manage business development opportunities from clinical trials and industry news
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowAddDialog(true)}
            data-testid="add-opportunity-btn"
          >
            <FileText className="h-4 w-4 mr-2" />
            Quick Add
          </Button>
          <Button
            variant="outline"
            onClick={learnFromEmails}
            title="Train email templates from BD team emails"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Train Templates
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={searchRunning} data-testid="run-search-btn">
                {searchRunning ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Run Search
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => runSearch("clinical-trials")}>
                <Beaker className="h-4 w-4 mr-2" />
                ClinicalTrials.gov
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => runSearch("ai-news")}>
                <Sparkles className="h-4 w-4 mr-2" />
                AI News Search
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => runSearch("web-news")}>
                <Search className="h-4 w-4 mr-2" />
                Web News Search
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => runSearch("full")}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Run Full Search
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Email to Opportunity */}
          <Button 
            variant="outline" 
            onClick={processEmailsForOpportunities}
            disabled={processingEmails}
          >
            {processingEmails ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <MailPlus className="h-4 w-4 mr-2" />
            )}
            Email → Opp
          </Button>

          {/* Import from Excel */}
          <Button 
            variant="outline" 
            onClick={() => setShowImportDialog(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import Excel
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowWorkflowGuide(true)}
            title="BD Workflow Guide"
            data-testid="workflow-guide-btn"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              fetchEmailRules();
              setShowEmailRulesDialog(true);
            }}
            title="Email Parser Settings"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Total Opportunities</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.by_status?.New || 0}</div>
              <p className="text-xs text-muted-foreground">New</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-purple-600">{stats.by_status?.Watching || 0}</div>
              <p className="text-xs text-muted-foreground">Watching</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.Contacted || 0}</div>
              <p className="text-xs text-muted-foreground">Contacted</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-emerald-600">{stats.internal_company_count}</div>
              <p className="text-xs text-muted-foreground">CRM Companies</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{stats.recent_7_days}</div>
              <p className="text-xs text-muted-foreground">Last 7 Days</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by company, indication, or trial..."
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-9"
                data-testid="opportunities-search"
              />
            </div>
            <Select
              value={filters.action_status}
              onValueChange={(v) => setFilters(f => ({ ...f, action_status: v }))}
            >
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Watching">Watching</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Not Relevant">Not Relevant</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.therapeutic_area}
              onValueChange={(v) => setFilters(f => ({ ...f, therapeutic_area: v }))}
            >
              <SelectTrigger className="w-[180px]" data-testid="area-filter">
                <SelectValue placeholder="Therapeutic Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {THERAPEUTIC_AREAS.map(area => (
                  <SelectItem key={area} value={area}>{area}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.source}
              onValueChange={(v) => setFilters(f => ({ ...f, source: v }))}
            >
              <SelectTrigger className="w-[180px]" data-testid="source-filter">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {availableSources.map(source => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.signal_type}
              onValueChange={(v) => setFilters(f => ({ ...f, signal_type: v }))}
            >
              <SelectTrigger className="w-[180px]" data-testid="signal-type-filter">
                <SelectValue placeholder="Signal Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Signals</SelectItem>
                {SIGNAL_TYPES.map(s => (
                  <SelectItem key={s.key} value={s.key}>{s.key}: {s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filters.source !== "all" && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  if (window.confirm(`Delete ALL opportunities from "${filters.source}"? This cannot be undone.`)) {
                    bulkDeleteBySource(filters.source);
                  }
                }}
                title={`Delete all ${filters.source} opportunities`}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete All
              </Button>
            )}
            <Button
              variant={filters.internal_only ? "default" : "outline"}
              onClick={() => setFilters(f => ({ ...f, internal_only: !f.internal_only }))}
              data-testid="internal-filter"
            >
              <Building2 className="h-4 w-4 mr-2" />
              CRM Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Opportunities Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : opportunities.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No opportunities found</h3>
              <p className="text-muted-foreground mt-2">
                Run a search to discover new opportunities
              </p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
            {selectedOpps.size > 0 && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 border-b">
                <span className="text-sm font-medium">{selectedOpps.size} selected</span>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={bulkDeleteSelected}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-1" />
                  )}
                  Delete Selected
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedOpps(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox 
                      checked={opportunities.length > 0 && selectedOpps.size === opportunities.length}
                      onCheckedChange={selectAllOpps}
                    />
                  </TableHead>
                  <TableHead>Company / Sponsor</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Indication</TableHead>
                  <TableHead>Phase</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Signal</TableHead>
                  <TableHead>Leads</TableHead>
                  <TableHead>Date Found</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.map((opp) => (
                  <TableRow 
                    key={opp.id} 
                    data-testid={`opportunity-row-${opp.id}`}
                    className={cn("group", selectedOpps.has(opp.id) && "bg-blue-50")}
                  >
                    <TableCell>
                      <Checkbox 
                        checked={selectedOpps.has(opp.id)}
                        onCheckedChange={() => toggleSelectOpp(opp.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium flex items-center gap-2">
                        {editingCompany === opp.id ? (
                          <div className="flex items-center gap-1.5 w-full relative">
                            <Input
                              value={editCompanyName}
                              onChange={(e) => {
                                setEditCompanyName(e.target.value);
                                searchEditCompanies(e.target.value);
                              }}
                              onFocus={() => editCompanyName?.length >= 2 && setShowEditCompanyDropdown(true)}
                              onBlur={() => setTimeout(() => setShowEditCompanyDropdown(false), 200)}
                              className="h-7 text-sm"
                              autoFocus
                              data-testid={`inline-edit-company-${opp.id}`}
                            />
                            {showEditCompanyDropdown && editCompanyOptions.length > 0 && (
                              <div className="absolute z-50 w-full top-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-auto">
                                {editCompanyOptions.slice(0, 6).map((company) => (
                                  <div
                                    key={company}
                                    className="px-3 py-1.5 hover:bg-[#E6F2ED] cursor-pointer text-xs flex items-center gap-2"
                                    onMouseDown={() => {
                                      setEditCompanyName(company);
                                      setShowEditCompanyDropdown(false);
                                    }}
                                  >
                                    <Building2 className="h-3 w-3 text-[#2D5A4C]" />
                                    {company}
                                  </div>
                                ))}
                              </div>
                            )}
                            <Button size="sm" className="h-7 w-7 p-0" onClick={() => saveCompanyEdit(opp.id)} disabled={savingCompany}>
                              {savingCompany ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingCompany(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            {opp.is_internal_company && (
                              <Building2 className="h-4 w-4 text-green-500" title="In CRM" />
                            )}
                            {opp.linked_company ? (
                              <Link 
                                to={`/companies/${encodeURIComponent(opp.linked_company)}`}
                                className="text-blue-600 hover:underline"
                              >
                                {opp.company_name}
                              </Link>
                            ) : (
                              opp.company_name
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCompany(opp.id);
                                setEditCompanyName(opp.company_name);
                              }}
                              data-testid={`edit-company-table-${opp.id}`}
                              title="Edit company"
                            >
                              <Settings className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                      {opp.nct_id && (
                        <div className="text-xs text-muted-foreground">
                          {opp.nct_id}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <EventTypeIcon type={opp.event_type} />
                        <span className="text-sm">{opp.event_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-[200px] truncate" title={opp.indication}>
                        {opp.indication || "—"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{opp.current_phase || "N/A"}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{opp.therapeutic_area || "—"}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {opp.source === "ClinicalTrials.gov" ? (
                          <Beaker className="h-3 w-3 text-blue-500" />
                        ) : opp.source === "AI Search" ? (
                          <Sparkles className="h-3 w-3 text-purple-500" />
                        ) : null}
                        <span className="text-xs text-muted-foreground">{opp.source || "Manual"}</span>
                      </div>
                    </TableCell>
                    <TableCell data-testid={`opp-signal-${opp.id}`}>
                      {opp.signal_type ? (() => {
                        const sig = getSignalInfo(opp.signal_type);
                        return sig ? (
                          <div className="flex flex-col gap-0.5">
                            <Badge className={cn("text-[10px] px-1.5 py-0 border", sig.color)} title={`${sig.label} (${sig.timeline})`}>
                              {sig.key}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground leading-tight">{sig.timeline}</span>
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">{opp.signal_type}</Badge>
                        );
                      })() : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`opp-leads-${opp.id}`}>
                      {opp.linked_leads && opp.linked_leads.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          {opp.linked_leads.slice(0, 2).map(lead => {
                            const style = LEAD_CONNECTION_STATUSES[lead.status] || LEAD_CONNECTION_STATUSES["New"];
                            return (
                              <div key={lead.id} className="flex items-center gap-1.5">
                                <span className={cn("h-1.5 w-1.5 rounded-full flex-shrink-0", style.dot)} title={lead.status} />
                                <Link
                                  to={`/leads/${lead.id}`}
                                  className="text-xs text-blue-600 hover:underline truncate max-w-[100px]"
                                  title={`${lead.name} (${lead.status})`}
                                >
                                  {lead.name}
                                </Link>
                              </div>
                            );
                          })}
                          {opp.linked_leads.length > 2 && (
                            <span className="text-xs text-muted-foreground">+{opp.linked_leads.length - 2} more</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 px-1 text-xs text-muted-foreground hover:text-blue-600"
                            onClick={(e) => { e.stopPropagation(); openLeadManager(opp); }}
                            data-testid={`manage-leads-${opp.id}`}
                          >
                            <Link2 className="h-3 w-3 mr-0.5" />
                            Manage
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs text-muted-foreground hover:text-blue-600"
                          onClick={(e) => { e.stopPropagation(); openLeadManager(opp); }}
                          data-testid={`link-leads-${opp.id}`}
                        >
                          <UserPlus className="h-3 w-3 mr-1" />
                          Link
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(opp.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="p-0 h-auto">
                            <StatusBadge status={opp.action_status} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          {Object.keys(ACTION_STATUS_STYLES).map(status => (
                            <DropdownMenuItem 
                              key={status}
                              onClick={() => updateOpportunityStatus(opp.id, status)}
                            >
                              {status}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {opp.source_url && (
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                          >
                            <a href={opp.source_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => generateEmailTemplate(opp)}
                          title="Generate Email"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedOpp(opp)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>
                                {editingCompany === opp.id ? (
                                  <div className="flex items-center gap-2 relative">
                                    <Input
                                      value={editCompanyName}
                                      onChange={(e) => {
                                        setEditCompanyName(e.target.value);
                                        searchEditCompanies(e.target.value);
                                      }}
                                      onFocus={() => editCompanyName?.length >= 2 && setShowEditCompanyDropdown(true)}
                                      onBlur={() => setTimeout(() => setShowEditCompanyDropdown(false), 200)}
                                      className="text-lg font-semibold"
                                      data-testid="edit-company-input"
                                    />
                                    {showEditCompanyDropdown && editCompanyOptions.length > 0 && (
                                      <div className="absolute z-50 w-full top-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                                        {editCompanyOptions.slice(0, 8).map((company) => (
                                          <div
                                            key={company}
                                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm font-normal"
                                            onMouseDown={() => {
                                              setEditCompanyName(company);
                                              setShowEditCompanyDropdown(false);
                                            }}
                                          >
                                            {company}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <Button size="sm" onClick={() => saveCompanyEdit(opp.id)} disabled={savingCompany} data-testid="save-company-btn">
                                      {savingCompany ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditingCompany(null)}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    {opp.company_name}
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6"
                                      onClick={() => {
                                        setEditingCompany(opp.id);
                                        setEditCompanyName(opp.company_name);
                                      }}
                                      title="Edit company name"
                                      data-testid="edit-company-btn"
                                    >
                                      <Settings className="h-3 w-3" />
                                    </Button>
                                  </div>
                                )}
                              </DialogTitle>
                              <DialogDescription>
                                {opp.event_type} • {opp.therapeutic_area}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium">Phase</label>
                                  <p className="text-sm text-muted-foreground">{opp.current_phase || "N/A"}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">NCT ID</label>
                                  <p className="text-sm text-muted-foreground">{opp.nct_id || "N/A"}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Indication</label>
                                  <p className="text-sm text-muted-foreground">{opp.indication || "N/A"}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Source</label>
                                  <p className="text-sm text-muted-foreground">{opp.source}</p>
                                </div>
                              </div>
                              {opp.signal_type && (() => {
                                const sig = getSignalInfo(opp.signal_type);
                                return (
                                  <div className="rounded-lg border p-3 bg-slate-50">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge className={cn("text-xs border", sig?.color || "bg-slate-100 text-slate-800")}>
                                        {opp.signal_type}: {sig?.label || "Unknown"}
                                      </Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                      <div>
                                        <span className="font-medium text-slate-600">Timeline:</span>{" "}
                                        <span className="text-slate-500">{opp.timeline_to_site_selection || sig?.timeline || "N/A"}</span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-slate-600">Signal Source:</span>{" "}
                                        <span className="text-slate-500">{opp.signal_source || sig?.sources || "N/A"}</span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                              {opp.ai_summary && (
                                <div>
                                  <label className="text-sm font-medium">AI Summary</label>
                                  <p className="text-sm text-muted-foreground mt-1">{opp.ai_summary}</p>
                                </div>
                              )}
                              {opp.trial_title && (
                                <div>
                                  <label className="text-sm font-medium">Trial Title</label>
                                  <p className="text-sm text-muted-foreground mt-1">{opp.trial_title}</p>
                                </div>
                              )}
                            </div>
                            <DialogFooter>
                              <Button onClick={() => generateEmailTemplate(opp)}>
                                <Mail className="h-4 w-4 mr-2" />
                                Generate Email
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteConfirm(opp.id)}
                          title="Delete opportunity"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Template Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Email Template</DialogTitle>
            <DialogDescription>
              {selectedOpp?.company_name} - {selectedOpp?.event_type}
            </DialogDescription>
          </DialogHeader>
          {emailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : emailTemplate ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Subject</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(emailTemplate.subject)}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <Input value={emailTemplate.subject} readOnly />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">Body</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(emailTemplate.body)}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
                <Textarea 
                  value={emailTemplate.body} 
                  readOnly 
                  className="min-h-[300px] font-mono text-sm"
                />
              </div>
              <div className="flex items-center justify-between">
                <Badge variant="outline">
                  {emailTemplate.template_type === "ai_learned" ? "AI Learned" : 
                   emailTemplate.template_type === "ai_generated" ? "AI Generated" : "Static Template"}
                  {emailTemplate.style && ` • ${emailTemplate.style === "initial" ? "Formal" : "Friendly"}`}
                </Badge>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateEmailTemplate(selectedOpp, "initial")}
                  >
                    Formal (Initial)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => generateEmailTemplate(selectedOpp, "followup")}
                  >
                    Friendly (Follow-up)
                  </Button>
                  <Button
                    onClick={() => copyToClipboard(`Subject: ${emailTemplate.subject}\n\n${emailTemplate.body}`)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy All
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Quick Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setSelectedLeads([]);
          setAllLeads([]);
          setShowAddLeadInline(false);
          setShowAddCompanyInline(false);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Opportunity</DialogTitle>
            <DialogDescription>
              Manually add a BD opportunity from your research. Start by entering the company name — existing companies will autocomplete. You can also create a new company and leads inline.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Company Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 relative">
                <label className="text-sm font-medium">Company Name *</label>
                <Input
                  placeholder="Start typing to search..."
                  value={newOpp.company_name}
                  onChange={(e) => {
                    setNewOpp(o => ({ ...o, company_name: e.target.value }));
                    searchCompanies(e.target.value);
                  }}
                  onFocus={() => newOpp.company_name?.length >= 2 && setShowCompanyDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                  data-testid="quick-add-company-input"
                />
                {showCompanyDropdown && companyOptions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-medium border-b bg-slate-50">Existing companies</div>
                    {companyOptions.slice(0, 10).map((company) => (
                      <div
                        key={company}
                        className="px-3 py-2 hover:bg-[#E6F2ED] cursor-pointer text-sm flex items-center gap-2"
                        onMouseDown={() => {
                          setNewOpp(o => ({ ...o, company_name: company }));
                          setShowCompanyDropdown(false);
                          fetchLeadsForCompany(company);
                        }}
                        data-testid={`company-option-${company}`}
                      >
                        <Building2 className="h-3.5 w-3.5 text-[#2D5A4C]" />
                        {company}
                      </div>
                    ))}
                  </div>
                )}
                {newOpp.company_name && companyOptions.length === 0 && !showAddCompanyInline && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 h-auto text-xs"
                    onClick={() => {
                      setShowAddCompanyInline(true);
                      setNewInlineCompany({ name: newOpp.company_name, tier: "Unranked" });
                    }}
                  >
                    + Company not found? Add as new company
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Event Type *</label>
                <Select
                  value={newOpp.event_type}
                  onValueChange={(v) => setNewOpp(o => ({ ...o, event_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Add Company Inline */}
            {showAddCompanyInline && (
              <Card className="border-dashed">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium">Add New Company</h4>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddCompanyInline(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder="Company Name"
                      value={newInlineCompany.name}
                      onChange={(e) => setNewInlineCompany(c => ({ ...c, name: e.target.value }))}
                    />
                    <Select 
                      value={newInlineCompany.tier} 
                      onValueChange={(v) => setNewInlineCompany(c => ({ ...c, tier: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Tier" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Gold">Gold</SelectItem>
                        <SelectItem value="Silver">Silver</SelectItem>
                        <SelectItem value="Bronze">Bronze</SelectItem>
                        <SelectItem value="Unranked">Unranked</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Company will be created when you add the opportunity
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 relative">
                <label className="text-sm font-medium">Indication</label>
                <Input
                  placeholder="e.g., Androgenetic Alopecia"
                  value={newOpp.indication}
                  onChange={(e) => {
                    setNewOpp(o => ({ ...o, indication: e.target.value }));
                    setShowIndicationDropdown(true);
                  }}
                  onFocus={() => newOpp.indication?.length >= 1 && setShowIndicationDropdown(true)}
                  onBlur={() => setTimeout(() => setShowIndicationDropdown(false), 200)}
                  data-testid="indication-input"
                />
                {showIndicationDropdown && newOpp.indication && filteredIndications.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    {filteredIndications.slice(0, 10).map((ind) => (
                      <div
                        key={ind}
                        className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                        onMouseDown={() => {
                          setNewOpp(o => ({ ...o, indication: ind }));
                          setShowIndicationDropdown(false);
                        }}
                      >
                        {ind}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Therapeutic Area</label>
                <Select
                  value={newOpp.therapeutic_area}
                  onValueChange={(v) => setNewOpp(o => ({ ...o, therapeutic_area: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    {THERAPEUTIC_AREAS.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Current Phase</label>
                <Input
                  placeholder="e.g., Phase 2a"
                  value={newOpp.current_phase}
                  onChange={(e) => setNewOpp(o => ({ ...o, current_phase: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Source</label>
                <Input
                  placeholder="e.g., PR Newswire, Dermatology Times"
                  value={newOpp.source}
                  onChange={(e) => setNewOpp(o => ({ ...o, source: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Source URL</label>
              <Input
                placeholder="https://..."
                value={newOpp.source_url}
                onChange={(e) => setNewOpp(o => ({ ...o, source_url: e.target.value }))}
              />
            </div>

            {/* Signal Type Section */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Signal Type</label>
                <Select
                  value={newOpp.signal_type}
                  onValueChange={(v) => {
                    const sig = getSignalInfo(v);
                    setNewOpp(o => ({
                      ...o,
                      signal_type: v,
                      timeline_to_site_selection: sig ? sig.timeline : o.timeline_to_site_selection,
                      signal_source: sig ? sig.sources : o.signal_source
                    }));
                  }}
                >
                  <SelectTrigger data-testid="signal-type-select">
                    <SelectValue placeholder="Select signal" />
                  </SelectTrigger>
                  <SelectContent>
                    {SIGNAL_TYPES.map(s => (
                      <SelectItem key={s.key} value={s.key}>
                        {s.key}: {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Timeline to Site Selection</label>
                <Input
                  placeholder="e.g., 3-6 months"
                  value={newOpp.timeline_to_site_selection}
                  onChange={(e) => setNewOpp(o => ({ ...o, timeline_to_site_selection: e.target.value }))}
                  data-testid="timeline-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Signal Source</label>
                <Input
                  placeholder="e.g., ClinicalTrials.gov"
                  value={newOpp.signal_source}
                  onChange={(e) => setNewOpp(o => ({ ...o, signal_source: e.target.value }))}
                  data-testid="signal-source-input"
                />
              </div>
            </div>

            {/* Leads Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Assign Leads</label>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto"
                  onClick={() => setShowAddLeadInline(!showAddLeadInline)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add New Lead
                </Button>
              </div>
              
              {allLeads.length > 0 ? (
                <div className="border rounded-md p-3 space-y-2">
                  <Input
                    placeholder="Search leads..."
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    className="h-8"
                  />
                  <div className="max-h-32 overflow-auto space-y-1">
                    {filteredLeads.slice(0, 10).map(lead => (
                      <div 
                        key={lead.id}
                        className={cn(
                          "flex items-center justify-between p-2 rounded text-sm cursor-pointer",
                          selectedLeads.includes(lead.id) ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-50"
                        )}
                        onClick={() => {
                          if (selectedLeads.includes(lead.id)) {
                            setSelectedLeads(prev => prev.filter(id => id !== lead.id));
                          } else {
                            setSelectedLeads(prev => [...prev, lead.id]);
                          }
                        }}
                      >
                        <div>
                          <span className="font-medium">{lead.contact || `${lead.first_name} ${lead.last_name}`}</span>
                          <span className="text-muted-foreground ml-2">{lead.role}</span>
                        </div>
                        {selectedLeads.includes(lead.id) && (
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedLeads.length > 0 && (
                    <p className="text-xs text-muted-foreground">{selectedLeads.length} lead(s) selected</p>
                  )}
                </div>
              ) : newOpp.company_name ? (
                <p className="text-sm text-muted-foreground">No leads found for this company. You can add one below.</p>
              ) : (
                <p className="text-sm text-muted-foreground">Select a company first to see available leads</p>
              )}
              
              {/* Add Lead Inline */}
              {showAddLeadInline && (
                <Card className="border-dashed">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium">Add New Lead</h4>
                      <Button variant="ghost" size="sm" onClick={() => setShowAddLeadInline(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="First Name"
                        value={newInlineLead.first_name}
                        onChange={(e) => setNewInlineLead(l => ({ ...l, first_name: e.target.value }))}
                      />
                      <Input
                        placeholder="Last Name"
                        value={newInlineLead.last_name}
                        onChange={(e) => setNewInlineLead(l => ({ ...l, last_name: e.target.value }))}
                      />
                      <Input
                        placeholder="Email"
                        value={newInlineLead.email}
                        onChange={(e) => setNewInlineLead(l => ({ ...l, email: e.target.value }))}
                      />
                      <Input
                        placeholder="Role/Title"
                        value={newInlineLead.role}
                        onChange={(e) => setNewInlineLead(l => ({ ...l, role: e.target.value }))}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Lead will be created for {newOpp.company_name || "the selected company"}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Key details, BD angle, timeline..."
                value={newOpp.notes}
                onChange={(e) => setNewOpp(o => ({ ...o, notes: e.target.value }))}
                className="min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createOpportunity}>
              Add Opportunity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Opportunity</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this opportunity? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => deleteOpportunity(deleteConfirm)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Connection Manager Dialog */}
      <Dialog open={showLeadManager} onOpenChange={(open) => {
        setShowLeadManager(open);
        if (!open) {
          setManagingOpp(null);
          setLeadSearchQuery("");
          setLeadSearchResults([]);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lead-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Manage Linked Leads
            </DialogTitle>
            <DialogDescription>
              {managingOpp?.company_name} — {managingOpp?.event_type}. Link contacts and track outreach progress per lead.
            </DialogDescription>
          </DialogHeader>

          {/* Current linked leads */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Linked Leads ({managingOpp?.linked_leads?.length || 0})</h4>
            {managingOpp?.linked_leads && managingOpp.linked_leads.length > 0 ? (
              <div className="space-y-2">
                {managingOpp.linked_leads.map(lead => {
                  const style = LEAD_CONNECTION_STATUSES[lead.status] || LEAD_CONNECTION_STATUSES["New"];
                  return (
                    <div key={lead.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`linked-lead-${lead.id}`}>
                      <div className="flex items-center gap-3">
                        <span className={cn("h-2.5 w-2.5 rounded-full", style.dot)} />
                        <div>
                          <Link to={`/leads/${lead.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                            {lead.name}
                          </Link>
                          {lead.linked_at && (
                            <p className="text-xs text-muted-foreground">Linked {formatDate(lead.linked_at)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={lead.status}
                          onValueChange={(val) => updateConnectionStatus(lead.id, val)}
                        >
                          <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`lead-status-select-${lead.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(LEAD_CONNECTION_STATUSES).map(status => (
                              <SelectItem key={status} value={status}>
                                <div className="flex items-center gap-1.5">
                                  <span className={cn("h-2 w-2 rounded-full", LEAD_CONNECTION_STATUSES[status].dot)} />
                                  {status}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => unlinkLead(lead.id)}
                          title="Unlink lead"
                          data-testid={`unlink-lead-${lead.id}`}
                        >
                          <Unlink className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-2">No leads linked yet. Search below to link leads.</p>
            )}
          </div>

          {/* Search and link new leads */}
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-medium">Link a Lead</h4>
            <Input
              placeholder="Search leads by name, email, or company..."
              value={leadSearchQuery}
              onChange={(e) => searchLeadsForLinking(e.target.value)}
              data-testid="lead-link-search"
            />
            {leadSearchResults.length > 0 && (
              <div className="border rounded-md max-h-48 overflow-auto">
                {leadSearchResults
                  .filter(lead => !(managingOpp?.linked_leads || []).some(ll => ll.id === lead.id))
                  .map(lead => (
                    <div
                      key={lead.id}
                      className="flex items-center justify-between p-2.5 hover:bg-slate-50 border-b last:border-0"
                    >
                      <div>
                        <span className="text-sm font-medium">{lead.contact || `${lead.first_name} ${lead.last_name}`}</span>
                        <span className="text-xs text-muted-foreground ml-2">{lead.company}</span>
                        {lead.role && <span className="text-xs text-muted-foreground ml-1">· {lead.role}</span>}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => linkLead(lead.id)}
                        disabled={linkingLead}
                        data-testid={`link-lead-btn-${lead.id}`}
                      >
                        <UserPlus className="h-3 w-3 mr-1" />
                        Link
                      </Button>
                    </div>
                  ))}
                {leadSearchResults.filter(lead => !(managingOpp?.linked_leads || []).some(ll => ll.id === lead.id)).length === 0 && (
                  <p className="text-xs text-muted-foreground p-3">All matching leads are already linked.</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Parser Rules Dialog */}
      <Dialog open={showEmailRulesDialog} onOpenChange={setShowEmailRulesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MailPlus className="h-5 w-5" />
              Email-to-Opportunity Rules
            </DialogTitle>
            <DialogDescription>
              Configure which emails automatically create opportunities. Emails matching these rules 
              will be parsed and converted to opportunities when you click "Email → Opp".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[400px] overflow-auto">
            {emailRules.map((rule) => (
              <div key={rule.id} className="flex items-start justify-between p-4 border rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{rule.name}</h4>
                    <Badge variant={rule.enabled ? "default" : "secondary"}>
                      {rule.enabled ? "Active" : "Disabled"}
                    </Badge>
                    <Badge variant="outline">{rule.priority}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Creates: <span className="font-medium">{rule.event_type}</span>
                  </p>
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p>Subject patterns: {rule.subject_patterns?.length || 0}</p>
                    <p>Body patterns: {rule.body_patterns?.length || 0}</p>
                  </div>
                </div>
                <Button
                  variant={rule.enabled ? "outline" : "default"}
                  size="sm"
                  onClick={() => toggleEmailRule(rule.id)}
                >
                  {rule.enabled ? "Disable" : "Enable"}
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <p className="text-xs text-muted-foreground flex-1">
              Tip: Forward industry press releases to your synced email to auto-create opportunities
            </p>
            <Button variant="outline" onClick={() => setShowEmailRulesDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportFile(null);
          setImportPreview(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import Opportunities from Excel
            </DialogTitle>
            <DialogDescription>
              Upload your monthly opportunity list. The system will auto-detect columns and skip duplicates.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Download Template */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Need a template?</p>
                <p className="text-xs text-muted-foreground">Download our Excel template with the right columns</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Template
              </Button>
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
                id="opp-file-upload"
              />
              <label htmlFor="opp-file-upload" className="cursor-pointer">
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-medium">
                  {importFile ? importFile.name : "Click to upload Excel/CSV file"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports .xlsx, .xls, .csv
                </p>
              </label>
            </div>

            {/* Preview Results */}
            {importPreview && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <Card>
                    <CardContent className="pt-3 pb-2">
                      <div className="text-2xl font-bold text-green-600">{importPreview.new_opportunities}</div>
                      <p className="text-xs text-muted-foreground">New Opportunities</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-3 pb-2">
                      <div className="text-2xl font-bold text-yellow-600">{importPreview.duplicates}</div>
                      <p className="text-xs text-muted-foreground">Duplicates (skip)</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-3 pb-2">
                      <div className="text-2xl font-bold">{importPreview.total_rows}</div>
                      <p className="text-xs text-muted-foreground">Total Rows</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="text-xs text-muted-foreground">
                  Columns detected: {importPreview.columns_found?.join(", ") || "None"}
                </div>

                {/* Sample Data Preview */}
                {importPreview.sample_data?.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-muted px-3 py-2 text-sm font-medium">Preview (first 10 rows)</div>
                    <div className="max-h-[200px] overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Company</TableHead>
                            <TableHead>Event Type</TableHead>
                            <TableHead>Indication</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importPreview.sample_data.map((row, idx) => (
                            <TableRow key={idx} className={row.is_duplicate ? "bg-yellow-50" : ""}>
                              <TableCell className="font-medium">{row.company_name}</TableCell>
                              <TableCell>{row.event_type}</TableCell>
                              <TableCell>{row.indication || "—"}</TableCell>
                              <TableCell>
                                {row.is_duplicate ? (
                                  <Badge variant="outline" className="text-yellow-600">Duplicate</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-green-600">New</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={executeImport} 
              disabled={!importPreview || importPreview.new_opportunities === 0 || importing}
            >
              {importing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Import {importPreview?.new_opportunities || 0} Opportunities
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workflow Guide Dialog */}
      <Dialog open={showWorkflowGuide} onOpenChange={setShowWorkflowGuide}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="workflow-guide-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-600" />
              BD Opportunity Workflow
            </DialogTitle>
            <DialogDescription>
              End-to-end guide for your team — from discovery to lead outreach
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 text-sm">
            {/* Step 1 */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">1</span>
                <h4 className="font-semibold">Opportunity Identified</h4>
              </div>
              <p className="text-muted-foreground ml-8">
                Opportunities come in through multiple channels:
              </p>
              <ul className="ml-12 space-y-1 text-muted-foreground list-disc">
                <li><strong>Run Search</strong> — Automated scans of ClinicalTrials.gov, AI News, and Web News</li>
                <li><strong>Email → Opp</strong> — Parses your synced inbox for daily intelligence briefs</li>
                <li><strong>Import Excel</strong> — Bulk import from monthly opportunity lists</li>
                <li><strong>Quick Add</strong> — Manually enter an opportunity you found elsewhere</li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">2</span>
                <h4 className="font-semibold">Company Assigned</h4>
              </div>
              <p className="text-muted-foreground ml-8">
                Every opportunity is tied to a company. When you create one:
              </p>
              <ul className="ml-12 space-y-1 text-muted-foreground list-disc">
                <li><strong>Existing company</strong> — Start typing in the Company field and select from autocomplete. The opportunity auto-links to your CRM company.</li>
                <li><strong>New company</strong> — If no match appears, click <em>"+ Company not found? Add as new company"</em> to create it inline with a tier assignment.</li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">3</span>
                <h4 className="font-semibold">Leads Linked</h4>
              </div>
              <p className="text-muted-foreground ml-8">
                Link your contacts at the company to track outreach progress:
              </p>
              <ul className="ml-12 space-y-1 text-muted-foreground list-disc">
                <li><strong>During Quick Add</strong> — Select existing leads from the list, or click <em>"Add New Lead"</em> to create one inline with name, email, and role.</li>
                <li><strong>After creation</strong> — Click the <strong>Link</strong> button (or <strong>Manage</strong>) in the Leads column of any opportunity. Search for leads and link them.</li>
              </ul>
            </div>

            {/* Step 4 */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">4</span>
                <h4 className="font-semibold">Track Connection Status</h4>
              </div>
              <p className="text-muted-foreground ml-8">
                Each lead-opportunity link has a connection status. Update it as your outreach progresses:
              </p>
              <div className="ml-12 grid grid-cols-2 gap-x-4 gap-y-1.5 mt-1">
                {Object.entries(LEAD_CONNECTION_STATUSES).map(([status, style]) => (
                  <div key={status} className="flex items-center gap-2">
                    <span className={cn("h-2.5 w-2.5 rounded-full", style.dot)} />
                    <span className="text-muted-foreground">{status}</span>
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground ml-8 mt-1.5">
                Use the <strong>Manage</strong> button in the Leads column to change statuses or unlink leads.
              </p>
            </div>

            {/* Step 5 */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">5</span>
                <h4 className="font-semibold">Outreach & Follow-up</h4>
              </div>
              <p className="text-muted-foreground ml-8">
                Generate personalized outreach emails using the <strong>email icon</strong> in the Actions column. Templates are trained from your team's past BD emails. Track follow-up dates on each lead's detail page.
              </p>
            </div>

            {/* Tip */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 ml-0">
              <p className="text-blue-800 text-xs">
                <strong>Tip:</strong> Use the <strong>Status</strong> dropdown on each opportunity row to track overall progress (New → Watching → Contacted → In Progress). Use <strong>Lead Connection Status</strong> to track individual contact progress.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
